import { makeAutoObservable } from 'mobx';

import { themeBackground } from '@shared/styles/tokens';
import { AppTheme } from '@shared/types';

// Те же ключ и системная настройка — в инлайн-скрипте index.html (до загрузки бандла);
// совпадение проверяет store.test.ts
export const THEME_STORAGE_KEY = 'rpso-theme';
export type ThemePreference = AppTheme | 'system';

export const parseTheme = (value: unknown): AppTheme | null =>
  value === AppTheme.Light || value === AppTheme.Dark ? value : null;

// Тему уже выбрал инлайн-скрипт и записал в data-theme; на сервере (пререндер) — тёмная
const readInitialTheme = (): AppTheme =>
  (typeof document !== 'undefined' && parseTheme(document.documentElement.dataset.theme)) ||
  AppTheme.Dark;

const readPreference = (): ThemePreference => {
  try {
    return parseTheme(localStorage.getItem(THEME_STORAGE_KEY)) ?? 'system';
  } catch {
    return 'system';
  }
};

class ThemeStore {
  currentTheme: AppTheme = readInitialTheme();
  preference: ThemePreference = readPreference();

  constructor() {
    makeAutoObservable(this);
  }

  setTheme = (type: AppTheme): void => {
    this.preference = type;
    this.applyTheme(type);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, type);
    } catch {
      /* Выбор действует и при запрете хранилища. */
    }
  };

  syncSystemTheme = (): void => {
    if (this.preference === 'system') this.applyTheme(this.systemTheme());
  };

  private systemTheme = (): AppTheme =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? AppTheme.Dark : AppTheme.Light;

  private applyTheme = (type: AppTheme): void => {
    this.currentTheme = type;
    // Цвета — CSS-переменные по data-theme: React-дерево перерисовывать не нужно
    document.documentElement.dataset.theme = type;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', themeBackground(type));
  };

  toggle = (): void => {
    this.setTheme(this.currentTheme === AppTheme.Dark ? AppTheme.Light : AppTheme.Dark);
  };

  get isDark(): boolean {
    return this.currentTheme === AppTheme.Dark;
  }
}

export const themeStore = new ThemeStore();
